import { defineComponent as _defineComponent } from 'vue'
import { computed, onUnmounted, reactive, watch } from 'vue';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import CInput from '@/components/shared/CInput';
import CButton from '@/components/shared/CButton/CButton.vue';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import type { CanvasEx, PastedVector } from '@/typings/canvas';
import type { ActiveSelection, UserVector, VectorPlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { ElementMapOption } from './types';
import type { KeyValueDto } from '@/typings/keyValueDto';
import type { LibraryDetailElement } from '@/typings/libraries';
import { getSvgStringFromPath, uploadVectorToS3 } from '@/utils/vectorUtils';
import { getExtension } from '@/utils/pathUtils';
import { runTasksInChunks } from '@/utils/promiseUtils';
import { getLibraryElementDetails } from '@/api/libraries';


export default /*#__PURE__*/_defineComponent({
  __name: 'DynamicVectorProperties',
  props: {
    canvas: null,
    vectorElement: null,
    editMode: { type: Boolean },
    previewMode: { type: Boolean },
    selectedGroup: null
  },
  emits: ["open-vector-library-selector", "trigger-loading", "display-sample-option", "image-replace"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'open-vector-library-selector'): void;
    (e: 'trigger-loading', isLoading: boolean, msg?: string): void;
    (e: 'display-sample-option', element: VectorPlaceholderElement): void;
    (e: 'image-replace'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    canvas: CanvasEx;
    vectorElement?: VectorPlaceholderElement;
    editMode?: boolean;
    previewMode?: boolean;
    selectedGroup?: ActiveSelection<VectorPlaceholderElement>;
};

const VECTOR_TYPE = LibraryTypeEnum.Vector;





const model = reactive({
    vectors: [] as UserVector[],
    elementId: undefined as number | undefined,
    vectorOption: 0 as number | undefined,
    vectorMap: new Map<number, string>(),
    vectorLibraryId: undefined as number | undefined,
    isLibraryIdMixed: false,
    isVectorMapMixed: false,
    isElementIdMixed: false
});

const cachedSvg = new Map<string, string>();

const disabled = computed(() => {
    const element = props.vectorElement;
    let state = { common: false, elementId: false };

    if (!element) return state;

    state = { common: !!element.locked, elementId: !!element.locked || props.previewMode || !element.uuid };
    return state;
});

const onVectorPasted = ({ element, vectors }: PastedVector) => onVectorUpload(vectors, undefined, element);
watch(
    () => props.canvas,
    (canvas) => {
        if (!canvas) return;
        canvas.on('vectors:pasted', onVectorPasted);
    }
);

watch(
    () => props.vectorElement,
    (element) => {
        if (!element || element.starMapConfig || element.allowUserUpload) return;

        model.elementId = element.id;
        model.vectorOption = element.selectedPosition;
        model.vectorLibraryId = element.vectorLibraryId;
        initVectorMap(element.vectors);
    }
);

const initVectorMap = (vectors: UserVector[]) => {
    model.vectors = [...vectors];
    model.vectorMap = new Map(vectors.map((v) => [v.position + 1, v.svgPath]));
};

const isVectorMapMixed = (firstMap: Map<number, string>, secondMap: UserVector[]): boolean => {
    if (firstMap.size !== secondMap.length) return true;
    return secondMap.some((v) => firstMap.get(v.position + 1) !== v.svgPath);
};

watch(
    () => props.selectedGroup,
    (group) => {
        const objects = group?.getObjects();
        if (!objects?.length) return;

        const firstElement = objects[0];
        model.vectorOption = firstElement.selectedPosition || 1;
        initVectorMap(firstElement.vectors);
        for (const element of objects) {
            if (model.isVectorMapMixed && model.isLibraryIdMixed && model.isElementIdMixed) break;

            if (element.id !== firstElement.id) {
                model.isElementIdMixed = true;
            }

            if (element.vectorLibraryId !== firstElement.vectorLibraryId) {
                model.isLibraryIdMixed = true;
            }

            if (!model.isVectorMapMixed) {
                model.isVectorMapMixed = isVectorMapMixed(model.vectorMap, element.vectors);
            }

            if (model.vectorOption !== element.selectedPosition) {
                model.vectorOption = undefined;
            }
        }

        if (!model.isElementIdMixed) model.elementId = firstElement.id;
        if (!model.isLibraryIdMixed) model.vectorLibraryId = firstElement.vectorLibraryId;
        if (model.isVectorMapMixed) initVectorMap([]);
    },
    { immediate: true }
);

const displayLoader = (active = true, message?: string) => emit('trigger-loading', active, message);

const onVectorUpload = async (files: File[], position?: number, el?: VectorPlaceholderElement) => {
    const element = el || props.vectorElement || props.selectedGroup?.getObjects()[0];
    if (!element) return;

    let imgPosition = position ? position - 1 : model.vectors.length;
    const vectors = model.isVectorMapMixed ? [] : [...element.vectors];

    try {
        for (const file of files) {
            const selectedVector = position ? vectors.find((v) => v.position == imgPosition) : undefined;
            const extension = getExtension(file.name);
            let baseLoaderMsg = `Uploading ${extension.toUpperCase()} '${file.name}'`;
            displayLoader(true, baseLoaderMsg);
            const { SVG, EPS } = await uploadVectorToS3(file, false, false, undefined, (type, progress) => {
                if (type == 'upload') {
                    displayLoader(true, `${baseLoaderMsg} ${progress}%`);
                    return;
                }

                displayLoader(true, 'Processing');
            });

            if (selectedVector) {
                selectedVector.svgPath = SVG;
                selectedVector.epsPath = EPS;
                imgPosition++;
                continue;
            }

            vectors.push({
                svgPath: SVG,
                epsPath: EPS,
                position: imgPosition
            });
            imgPosition++;
        }

        initVectorMap(vectors);
        const lastVector = vectors.at(-1)!;
        await updateElement((el) => updateElementVectors(el, vectors, lastVector.position + 1), el ? [el] : []);
        model.isVectorMapMixed = false;
    } catch (error) {
        console.error(error);
    } finally {
        displayLoader(false);
    }
};

const updateElement = async (
    callback: (element: VectorPlaceholderElement, list?: VectorPlaceholderElement[]) => void | Promise<void>,
    elementList?: VectorPlaceholderElement[]
) => {
    const promises: Promise<void>[] = [];
    const objects = props.selectedGroup ? props.selectedGroup.getObjects() : [props.vectorElement!];

    if (elementList?.length) {
        objects.push(...elementList);
    }

    for (const element of objects) {
        if (!element) continue;
        promises.push(Promise.resolve(callback(element, objects)));
    }
    await runTasksInChunks(promises);
};

const cachedLibElements = new Map<string, LibraryDetailElement[]>();
const onLoadOption = async (option: number, value?: ElementMapOption) => {
    updateElement(async (el, list) => {
        let val = value;
        if (!list || list.length < 2) {
            return loadVectorIntoElement(option, val, el);
        }

        if (el.vectorLibraryId) {
            const id = `${el.vectorLibraryId}-${option}`;
            const libElement =
                cachedLibElements.get(id) ||
                (await getLibraryElementDetails(el.vectorLibraryId as number, VECTOR_TYPE, undefined, '', option));
            if (!libElement.length) return;
            cachedLibElements.set(id, libElement);
            val = libElement[0].path;
        } else {
            val = el.vectors.find((v) => v.position === option - 1)?.svgPath;
        }
        return loadVectorIntoElement(option, val, el);
    });
};

const loadVectorIntoElement = async (option: number, value?: ElementMapOption, el?: VectorPlaceholderElement) => {
    const element = el || props.vectorElement;
    if (!element) return;

    const vector = (value as string) || model.vectorMap.get(option);

    if (!vector?.length) {
        element.selectedPosition = model.vectorOption = 1;
        emit('display-sample-option', element);
        return;
    }

    element.selectedPosition = option;
    model.vectorOption = option;

    const svgString = cachedSvg.get(vector) || (await getSvgStringFromPath(vector));
    cachedSvg.set(vector, svgString);

    element.set('svgString', svgString);
    props.canvas.requestRenderAll();
};

const removeVectorOptionFromMap = (position: number) => {
    const oldMap = model.vectorMap;
    updateElement((element) => {
        const vectors = element.vectors.filter((v) => v.position != position - 1);
        return updateElementVectors(element, vectors);
    });
    oldMap.delete(position);
    model.vectorMap = new Map(oldMap);
};

const onVectorOptionsReordered = (options: KeyValueDto<number, ElementMapOption>[]) => {
    const element = props.vectorElement;
    if (!element) return;

    const vectors: UserVector[] = [];
    let newPosition = 1;
    const userVectors = new Map(model.vectors.map((v) => [v.position + 1, v]));

    options.forEach(({ id }, index) => {
        const vector = userVectors.get(id!)!;

        vectors.push({ ...vector, position: index });

        if (id === model.vectorOption) {
            newPosition = index + 1;
        }
    });

    newPosition = props.selectedGroup ? 1 : newPosition;
    updateElement((el) => updateElementVectors(el, vectors, newPosition));

    initVectorMap(vectors);
};

const removeVectorLibrary = () => {
    updateElement((element) => {
        element.vectorLibraryId = undefined;
        if (props.editMode) {
            element.setEditedProp('vectorLibraryId');
        }
        return loadVectorIntoElement(1, undefined, element);
    });
    model.isLibraryIdMixed = false;
    model.vectorLibraryId = undefined;
};

const updateElementVectors = (element: VectorPlaceholderElement, vectors: UserVector[], position = 1) => {
    element.vectors = [...vectors];
    if (props.editMode) {
        element.setEditedProp('vectors');
    }
    return loadVectorIntoElement(position, undefined, element);
};

const updateElementId = () => {
    updateElement((element) => {
        element.id = model.elementId as number;
    });
    model.isElementIdMixed = false;
};

onUnmounted(() => {
    props.canvas.off('vectors:pasted', onVectorPasted);
});

const currentSlot = computed(() => (model.vectorLibraryId ? 'header' : 'actions'));
const elementIdPlaceholder = computed(() => (model.isElementIdMixed ? 'Mixed' : '1'));

return { __sfc: true,VECTOR_TYPE, props, emit, model, cachedSvg, disabled, onVectorPasted, initVectorMap, isVectorMapMixed, displayLoader, onVectorUpload, updateElement, cachedLibElements, onLoadOption, loadVectorIntoElement, removeVectorOptionFromMap, onVectorOptionsReordered, removeVectorLibrary, updateElementVectors, updateElementId, currentSlot, elementIdPlaceholder, ElementLibrarySelector, CInput, CButton }
}

})